import React, { useRef } from 'react';
import { Typography } from '~/UIKit';
import { useItemCreationSubsectionInput } from './useItemCreationSubsectionInput';
import { ItemCreationSubsectionInputStyles } from './ItemCreationSubsectionInput.styles';
import {
  IItemCreationSubsectionInputExtraProps,
  IItemCreationSubsectionInputProps,
  ItemCreationValidInput,
} from './ItemCreationSubsectionInput.model';
import { ItemCreationToolTip } from '~/Components';
import { connect, FormikContextType, getIn } from 'formik';
import { classnames } from 'tss-react/tools/classnames';

const simpleInputElement = (type: ItemCreationValidInput) =>
  [
    ItemCreationValidInput.TEXT,
    ItemCreationValidInput.NUMBER,
    ItemCreationValidInput.DATE,
    ItemCreationValidInput.TIME,
    ItemCreationValidInput.SELECT,
  ].includes(type);

export const ItemCreationSubsectionInput = connect<
  IItemCreationSubsectionInputProps<any> & IItemCreationSubsectionInputExtraProps,
  any
>(
  ({
    icon,
    label,
    name,
    type,
    children,
    placeholder,
    secondaryName,
    secondaryPlaceholder,
    noIconBackground,
    secondaryDisabled,
    options,
    InputProps,
    disabled,
    hasAnError,
    displayManipulation,
    secondaryValueDisplayManipulation,
    hidden,
    formik,
    customElement,
    isAChild,
    customElementFullSection,
    noBorder,
    showErrorLabel = false,
    isEdit = false,
    tooltip,
    note,
  }: IItemCreationSubsectionInputProps<any> & {
    formik: FormikContextType<any>;
  } & IItemCreationSubsectionInputExtraProps) => {
    const InputElement = useItemCreationSubsectionInput({
      type,
      icon,
      name: name as string,
      label: label as string,
      placeholder: placeholder || '',
      secondaryName: secondaryName as string,
      secondaryPlaceholder: secondaryPlaceholder || '',
      secondaryDisabled,
      options,
      InputProps,
      disabled: disabled || formik.isSubmitting,
    });

    const error = getIn(formik.errors, name as string) || (hasAnError && ' ');
    const value = getIn(formik.values, name as string);
    const dirty = getIn(formik.dirty, name as string);
    const touched = getIn(formik.touched, name as string) || isEdit;
    const secondaryError = secondaryName ? getIn(formik.errors, secondaryName as string) : '';
    const secondaryValue = secondaryName ? getIn(formik.values, secondaryName as string) : null;
    const secondaryDirty = secondaryName ? getIn(formik.dirty, name as string) : false;

    const { classes } = ItemCreationSubsectionInputStyles({
      isAChild: !!isAChild,
      icon,
      noIconBackground,
      disabled: !!disabled,
      hidden,
      error: touched && error,
      noBorder,
    })();
    const containerRef = useRef(null);
    if (customElement) {
      return hidden ? null : (
        <>
          <div
            className={classnames([
              { [classes.ItemCreationSubsectionCustomElement]: customElementFullSection },
            ])}
          >
            {customElement}
          </div>
          {showErrorLabel && name && touched && !!error && (
            <label className={classes.ItemCreationSubsectionErrorLabel}>{`${error}`}</label>
          )}
        </>
      );
    }
    return (
      <div className={classes.ItemCreationSubsectionInputContainer} ref={containerRef}>
        <div className={classes.ItemCreationSubsectionSingleInputContainer}>
          {!simpleInputElement(type || ItemCreationValidInput.TEXT) && !isAChild && icon && (
            <div className={classes.ItemCreationSubsectionInputIcon}>{icon}</div>
          )}
          {!simpleInputElement(type || ItemCreationValidInput.TEXT) && (
            <span className={classes.ItemCreationSubsectionLabelTooltip}>
              {(typeof label === 'string' && (
                <Typography className={classes.ItemCreationSubsectionTypographyBody}>
                  {label}
                </Typography>
              )) ||
                label}
              <ItemCreationToolTip {...tooltip} containerRef={containerRef} />
            </span>
          )}
          <InputElement
            {...{
              error,
              value: displayManipulation?.(value) || value || '',
              dirty,
              touched,
              setFieldValue: formik.setFieldValue,
              secondaryError,
              secondaryValue: secondaryValueDisplayManipulation?.(secondaryValue) || secondaryValue,
              secondaryDirty,
              disabled: formik.isSubmitting,
              secondaryDisabled: formik.isSubmitting,
              InputProps,
            }}
          />
        </div>
        {showErrorLabel && touched && !!error && (
          <label className={classes.ItemCreationSubsectionErrorLabel}>{error}</label>
        )}
        {children?.map((child: any, index: number) => (
          <ItemCreationSubsectionInput
            {...child}
            isAChild
            key={`subsection_${name as string}_children_input_${index}_${child.key ?? ''}`}
          />
        ))}
        {!!note && note}
      </div>
    );
  },
);
